import { useEffect, useMemo, useState } from 'react';
import { useRow, useRows } from '@/hooks/useLive';
import { save, upsert } from '@/lib/crud';
import { date as fmtDate, num, tonnes as fmtTonnes, isoDate } from '@/lib/format';
import { Drawer } from '@/components/ui/Drawer';
import { Evidence } from '@/components/evidence/Evidence';
import { Button } from '@/components/ui/Button';
import { StatTile } from '@/components/ui/StatTile';
import {
  NumberInput, SelectInput, TextArea, TextInput,
} from '@/components/ui/Field';
import type { Blast, Material } from '@/types/models';
import type { BlastStock } from '../Blasts';

interface Adjustment {
  id: string;
  blast_id: string;
  adjusted_on: string;
  tonnes_delta: number;
  reason: string;
}

const STATUSES = [
  { value: 'planned', label: 'Planned' },
  { value: 'drilled', label: 'Drilled' },
  { value: 'charged', label: 'Charged' },
  { value: 'blasted', label: 'Blasted — available' },
  { value: 'in_production', label: 'In production' },
  { value: 'depleted', label: 'Depleted' },
  { value: 'closed', label: 'Closed' },
];

type Draft = Partial<Blast> & Record<string, unknown>;

export function BlastDrawer({
  blast, materials, onClose, locked,
}: { blast: BlastStock; materials: Material[]; onClose: () => void; locked?: string | null }) {
  const isNew = !blast.blast_id;
  const stored = useRow<Blast>('blasts', blast.blast_id);
  const locations = useRows<Record<string, unknown>>('locations', 'code');
  const adjustments = useRows<Adjustment>('blast_adjustments', 'adjusted_on');

  const [v, setV] = useState<Draft>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const [adjTonnes, setAdjTonnes] = useState<number | null>(null);
  const [adjReason, setAdjReason] = useState('');

  useEffect(() => {
    setV({
      status: 'blasted',
      waste_tonnes: 0,
      ...(stored ?? {}),
    });
  }, [stored, blast.blast_id]);

  const set = (name: string, value: unknown) => setV((d) => ({ ...d, [name]: value }));

  const material = materials.find((m) => m.id === v.material_id);
  const density = Number(v.density_t_m3 ?? material?.density_t_m3 ?? 0);
  const volume = Number(v.volume_m3 ?? 0);
  const estimated = Math.round(volume * density * 100) / 100;
  const usable = Math.max(
    (v.tonnes_actual != null ? Number(v.tonnes_actual) : estimated) - Number(v.waste_tonnes ?? 0),
    0,
  );

  const mine = useMemo(
    () => (adjustments ?? []).filter((a) => a.blast_id === blast.blast_id),
    [adjustments, blast.blast_id],
  );

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!v.volume_m3 && !v.tonnes_actual) {
      e.volume_m3 = 'Give a blasted volume, or a surveyed tonnage';
    }
    if (volume < 0) e.volume_m3 = 'Cannot be negative';
    if (density <= 0 && volume > 0) e.density_t_m3 = 'Density is required to convert volume to tonnes';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function submit() {
    if (!validate()) return;
    setBusy(true);
    const payload: Draft = { ...v };
    // Density is stamped on the blast so a later change to the material's
    // default never re-values blasts that are already on the ground.
    if (payload.density_t_m3 == null && material) payload.density_t_m3 = material.density_t_m3;
    if (blast.blast_id) payload.id = blast.blast_id;
    const ok = await save(
      () => upsert('blasts', payload as Record<string, unknown> & { id?: string }),
      'Blast saved',
    );
    setBusy(false);
    if (ok) onClose();
  }

  async function addAdjustment() {
    if (!adjTonnes || !adjReason.trim()) return;
    setBusy(true);
    const ok = await save(
      () => upsert('blast_adjustments', {
        blast_id: blast.blast_id,
        adjusted_on: isoDate(),
        tonnes_delta: adjTonnes,
        reason: adjReason.trim(),
      }),
      'Adjustment recorded',
    );
    setBusy(false);
    if (ok) { setAdjTonnes(null); setAdjReason(''); }
  }

  return (
    <Drawer
      open
      locked={locked}
      width={640}
      title={isNew ? 'New blast' : blast.blast_no}
      subtitle={isNew ? 'Blast register entry' : `Blasted ${fmtDate(blast.blast_date)}`}
      onClose={onClose}
      footer={
        <>
          <span className="spacer" />
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} loading={busy}>Save</Button>
        </>
      }
    >
      {!isNew && (
        <div className="grid grid--stats" style={{ marginBottom: 'var(--sp-3)' }}>
          <StatTile label="Usable" value={blast.tonnes_usable} unit="t" />
          <StatTile label="Consumed" value={blast.tonnes_consumed} unit="t" />
          <StatTile label="Remaining" value={blast.tonnes_remaining} unit="t" rag="green" />
          <StatTile label="First hauled" value={fmtDate(blast.first_production_date)} />
        </div>
      )}

      <div className="form-grid">
        <div className="form-section field--full">Blast</div>
        <TextInput id="b-no" label="Blast number" value={String(v.blast_no ?? '')}
          onChange={(x) => set('blast_no', x || null)}
          help={isNew ? 'Leave blank to number it automatically (BL-0001).' : undefined} />
        <TextInput id="b-date" label="Blast date" type="date" value={String(v.blast_date ?? '')}
          onChange={(x) => set('blast_date', x || null)} />

        <SelectInput id="b-loc" label="Location" value={(v.location_id as string) ?? null}
          options={(locations ?? []).map((l) => ({ value: String(l.id), label: `${l.code} — ${l.name}` }))}
          onChange={(x) => set('location_id', x)} />
        <SelectInput id="b-status" label="Status" value={(v.status as string) ?? 'blasted'}
          options={STATUSES} onChange={(x) => set('status', x)}
          help="Only Blasted and In production count toward available stock." />

        <TextInput id="b-bench" label="Bench" value={String(v.bench ?? '')}
          onChange={(x) => set('bench', x || null)} />
        <TextInput id="b-area" label="Area" value={String(v.area ?? '')}
          onChange={(x) => set('area', x || null)} />

        <div className="form-section field--full">Volume and tonnage</div>
        <SelectInput id="b-mat" label="Material" value={(v.material_id as string) ?? null}
          options={materials.filter((m) => m.is_active).map((m) => ({
            value: m.id, label: `${m.name} (${num(m.density_t_m3, 2)} t/m³)`,
          }))}
          onChange={(x) => {
            set('material_id', x);
            const m = materials.find((y) => y.id === x);
            if (m && v.density_t_m3 == null) set('density_t_m3', m.density_t_m3);
          }} />
        <NumberInput id="b-density" label="Density" suffix="t/m³" min={0.1} max={10} step={0.001}
          error={errors.density_t_m3}
          value={(v.density_t_m3 as number) ?? null}
          onChange={(x) => set('density_t_m3', x)}
          help={material ? `Material default ${num(material.density_t_m3, 3)}` : 'Blank uses the material default.'} />

        <NumberInput id="b-vol" label="Blasted volume" suffix="m³" min={0} step={100}
          error={errors.volume_m3}
          value={(v.volume_m3 as number) ?? null}
          onChange={(x) => set('volume_m3', x)} />
        <NumberInput id="b-waste" label="Waste" suffix="t" min={0} step={10}
          value={(v.waste_tonnes as number) ?? 0}
          onChange={(x) => set('waste_tonnes', x ?? 0)} />

        <NumberInput id="b-actual" label="Surveyed tonnage" suffix="t" min={0} step={10}
          value={(v.tonnes_actual as number) ?? null}
          onChange={(x) => set('tonnes_actual', x)}
          help="Overrides the volume × density estimate once surveyed." />
        <div className="engine-hours" style={{ marginBottom: 'var(--sp-3)' }}>
          <span className="engine-hours__value">{fmtTonnes(usable)}</span>
          <span className="engine-hours__label">
            t usable{estimated > 0 && ` · ${fmtTonnes(estimated)} t from volume`}
          </span>
        </div>

        <div className="form-section field--full">Drill and blast</div>
        <TextInput id="b-start" label="Production started" type="date"
          value={String(v.production_started_on ?? '')}
          onChange={(x) => set('production_started_on', x || null)} />
        <TextInput id="b-contractor" label="Contractor" value={String(v.contractor ?? '')}
          onChange={(x) => set('contractor', x || null)} />
        <NumberInput id="b-drill" label="Drill metres" suffix="m" min={0} step={10}
          value={(v.drill_metres as number) ?? null} onChange={(x) => set('drill_metres', x)} />
        <NumberInput id="b-exp" label="Explosive" suffix="kg" min={0} step={10}
          value={(v.explosive_kg as number) ?? null} onChange={(x) => set('explosive_kg', x)} />

        <div className="field--full">
          <TextArea id="b-comments" label="Comments" value={String(v.comments ?? '')}
            onChange={(x) => set('comments', x || null)} />
        </div>
      </div>

      {!isNew && (
        <>
          <div className="form-section">Survey adjustments</div>
          <p className="small muted" style={{ marginTop: 0 }}>
            Corrections to blast stock that did not come through the diary — a
            re-survey, a reclassification, material moved without a diary entry.
            Every adjustment needs a reason, because it changes the depletion
            forecast.
          </p>

          {mine.length > 0 && (
            <table className="table" style={{ marginBottom: 'var(--sp-3)' }}>
              <thead>
                <tr><th>Date</th><th style={{ textAlign: 'right' }}>Tonnes</th><th>Reason</th></tr>
              </thead>
              <tbody>
                {mine.map((a) => (
                  <tr key={a.id}>
                    <td>{fmtDate(a.adjusted_on)}</td>
                    <td style={{ textAlign: 'right' }} className="tabular">
                      {a.tonnes_delta > 0 ? '+' : ''}{fmtTonnes(a.tonnes_delta)}
                    </td>
                    <td>{a.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="form-grid">
            <NumberInput id="adj-t" label="Adjustment" suffix="t" step={10}
              value={adjTonnes} onChange={setAdjTonnes}
              help="Negative writes stock off; positive adds it back." />
            <TextInput id="adj-r" label="Reason" value={adjReason} onChange={setAdjReason} />
            <div style={{ paddingTop: 22 }}>
              <Button variant="secondary" onClick={addAdjustment} loading={busy}
                disabled={!adjTonnes || !adjReason.trim()}>
                Record adjustment
              </Button>
            </div>
          </div>
        </>
      )}

      <Evidence
        entityType="blast"
        entityId={blast.blast_id}
        label="Survey and photographs"
        help="The survey PDF the volume came from, and photographs of the muckpile."
      />
    </Drawer>
  );
}
